import {
  InterfaceElementName,
  InterfaceEventName,
  InterfaceSectionName,
  SwapEventName,
} from "@uniswap/analytics-events";
import { Currency, CurrencyAmount, Token } from "@taraswap/sdk-core";
import { UNIVERSAL_ROUTER_ADDRESS } from "@taraswap/universal-router-sdk";
import { useAccountDrawer } from "components/AccountDrawer/MiniPortfolio/hooks";
import { ButtonError, ButtonLight, ButtonPrimary } from "components/Button";
import { GrayCard } from "components/Card";
import Column, { AutoColumn } from "components/Column";
import { ConfirmSwapModal } from "components/ConfirmSwapModal";
import SwapCurrencyInputPanel from "components/CurrencyInputPanel/SwapCurrencyInputPanel";
import ErrorIcon from "components/Icons/Error";
import Row from "components/Row";
import TokenSafetyModal from "components/TokenSafety/TokenSafetyModal";
import PriceImpactModal from "components/swap/PriceImpactModal";
import PriceImpactWarning from "components/swap/PriceImpactWarning";
import SwapDetailsDropdown from "components/swap/SwapDetailsDropdown";
import confirmPriceImpactWithoutFee from "components/swap/confirmPriceImpactWithoutFee";
import { Field } from "components/swap/constants";
import {
  ArrowContainer,
  ArrowWrapper,
  Dots,
  OutputSwapSection,
  SwapSection,
} from "components/swap/styled";
import { CHAIN_INFO, useIsSupportedChainId } from "constants/chains";
import { useCurrencyInfo } from "hooks/Tokens";
import { useAccount } from "hooks/useAccount";
import { useIsSwapUnsupported } from "hooks/useIsSwapUnsupported";
import { useMaxAmountIn } from "hooks/useMaxAmountIn";
import usePermit2Allowance, { AllowanceState } from "hooks/usePermit2Allowance";
import usePrevious from "hooks/usePrevious";
import useSelectChain from "hooks/useSelectChain";
import { SwapResult, useSwapCallback } from "hooks/useSwapCallback";
import { useUSDPrice } from "hooks/useUSDPrice";
import useWrapCallback, { WrapErrorText } from "hooks/useWrapCallback";
import { Trans } from "i18n";
import JSBI from "jsbi";
import { formatSwapQuoteReceivedEventProperties } from "lib/utils/analytics";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown } from "react-feather";
import { useNavigate } from "react-router-dom";
import { Text } from "rebass";
import { useAppSelector } from "state/hooks";
import {
  InterfaceTrade,
  RouterPreference,
  TradeState,
} from "state/routing/types";
import { isClassicTrade } from "state/routing/utils";
import {
  useSwapActionHandlers,
  useSwapAndLimitContext,
  useSwapContext,
} from "state/swap/hooks";
import { CurrencyState } from "state/swap/types";
import { useTheme } from "styled-components";
import { ExternalLink, ThemedText } from "theme/components";
import { maybeLogFirstSwapAction } from "tracing/swapFlowLoggers";
import { SafetyLevel } from "uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { CurrencyInfo } from "uniswap/src/features/dataApi/types";
import Trace from "uniswap/src/features/telemetry/Trace";
import { sendAnalyticsEvent } from "uniswap/src/features/telemetry/send";
import { WrapType } from "uniswap/src/types/wrap";
import { logger } from "utilities/src/logger/logger";
import { useTrace } from "utilities/src/telemetry/trace/TraceContext";
import { computeFiatValuePriceImpact } from "utils/computeFiatValuePriceImpact";
import { NumberType, useFormatter } from "utils/formatNumbers";
import { maxAmountSpend } from "utils/maxAmountSpend";
import { largerPercentValue } from "utils/percent";
import { computeRealizedPriceImpact, warningSeverity } from "utils/prices";
import { didUserReject } from "utils/swapErrorToUserReadableMessage";
import { getIsReviewableQuote } from ".";
import { OutputTaxTooltipBody } from "./TaxTooltipBody";
import { SwapDisclaimer } from "components/swap/SwapDisclaimer";

const SWAP_FORM_CURRENCY_SEARCH_FILTERS = {
  showCommonBases: true,
};

interface SwapFormProps {
  disableTokenInputs?: boolean;
  onCurrencyChange?: (selected: CurrencyState) => void;
}

export function SwapForm({
  disableTokenInputs = false,
  onCurrencyChange,
}: SwapFormProps) {
  const { isDisconnected, chainId: connectedChainId } = useAccount();

  const trace = useTrace();

  const { chainId, prefilledState, currencyState, multichainUXEnabled } =
    useSwapAndLimitContext();
  const isSupportedChain = useIsSupportedChainId(chainId);
  const { swapState, setSwapState, derivedSwapInfo } = useSwapContext();
  const { typedValue, independentField } = swapState;

  // token warning stuff
  const prefilledInputCurrencyInfo = useCurrencyInfo(
    prefilledState.inputCurrency
  );
  const prefilledOutputCurrencyInfo = useCurrencyInfo(
    prefilledState.outputCurrency
  );
  const [dismissTokenWarning, setDismissTokenWarning] =
    useState<boolean>(false);
  const [showPriceImpactModal, setShowPriceImpactModal] =
    useState<boolean>(false);

  const handleConfirmTokenWarning = useCallback(() => {
    setDismissTokenWarning(true);
  }, []);

  // dismiss warning if all imported tokens are in active lists
  const urlTokensNotInDefault = useMemo(
    () =>
      prefilledInputCurrencyInfo && prefilledOutputCurrencyInfo
        ? [prefilledInputCurrencyInfo, prefilledOutputCurrencyInfo]
            .filter((token: CurrencyInfo) => {
              return (
                token.currency.isToken &&
                token.safetyLevel !== SafetyLevel.Verified
              );
            })
            .map((token: CurrencyInfo) => token.currency as Token)
        : [],
    [prefilledInputCurrencyInfo, prefilledOutputCurrencyInfo]
  );

  const theme = useTheme();

  // toggle wallet when disconnected
  const accountDrawer = useAccountDrawer();

  const {
    trade: { state: tradeState, trade, swapQuoteLatency },
    allowedSlippage,
    currencyBalances,
    parsedAmount,
    currencies,
    inputError: swapInputError,
    outputFeeFiatValue,
    inputTax,
    outputTax,
  } = derivedSwapInfo;

  const [inputTokenHasTax, outputTokenHasTax] = useMemo(
    () => [!inputTax.equalTo(0), !outputTax.equalTo(0)],
    [inputTax, outputTax]
  );

  useEffect(() => {
    // Force exact input if the user switches to an output token with tax
    if (outputTokenHasTax && independentField === Field.OUTPUT) {
      setSwapState((state) => ({
        ...state,
        independentField: Field.INPUT,
        typedValue: "",
      }));
    }
  }, [independentField, outputTokenHasTax, setSwapState, trade?.outputAmount]);

  const {
    wrapType,
    execute: onWrap,
    inputError: wrapInputError,
    isApproving,
  } = useWrapCallback(
    currencies[Field.INPUT],
    currencies[Field.OUTPUT],
    typedValue
  );

  const showWrap: boolean = wrapType !== WrapType.NOT_APPLICABLE;

  const parsedAmounts = useMemo(
    () =>
      showWrap
        ? {
            [Field.INPUT]: parsedAmount,
            [Field.OUTPUT]: parsedAmount,
          }
        : {
            [Field.INPUT]:
              independentField === Field.INPUT
                ? parsedAmount
                : trade?.inputAmount,
            [Field.OUTPUT]:
              independentField === Field.OUTPUT
                ? parsedAmount
                : trade?.outputAmount,
          },
    [independentField, parsedAmount, showWrap, trade]
  );

  const showFiatValueInput = Boolean(parsedAmounts[Field.INPUT]);
  const showFiatValueOutput = Boolean(parsedAmounts[Field.OUTPUT]);
  const getSingleUnitAmount = (currency?: Currency) => {
    if (!currency) {
      return;
    }
    return CurrencyAmount.fromRawAmount(
      currency,
      JSBI.BigInt(10 ** currency.decimals)
    );
  };

  const fiatValueInput = useUSDPrice(
    parsedAmounts[Field.INPUT] ?? getSingleUnitAmount(currencies[Field.INPUT]),
    currencies[Field.INPUT]
  );
  const fiatValueOutput = useUSDPrice(
    parsedAmounts[Field.OUTPUT] ??
      getSingleUnitAmount(currencies[Field.OUTPUT]),
    currencies[Field.OUTPUT]
  );

  const [routeNotFound, routeIsLoading, routeIsSyncing] = useMemo(
    () => [
      tradeState === TradeState.NO_ROUTE_FOUND,
      tradeState === TradeState.LOADING,
      tradeState === TradeState.LOADING && Boolean(trade),
    ],
    [trade, tradeState]
  );
  const fiatValueTradeInput = useUSDPrice(trade?.inputAmount);
  const fiatValueTradeOutput = useUSDPrice(trade?.outputAmount);
  const preTaxFiatValueTradeOutput = useUSDPrice(trade?.outputAmount);

  // Add this check to validate if USD prices are in sync with trade amounts
  const usdPricesAreStale = useMemo(() => {
    if (!trade?.inputAmount || !trade?.outputAmount) return false;

    const inputAmountDecimal = parseFloat(trade.inputAmount.toFixed(2));
    const outputAmountDecimal = parseFloat(trade.outputAmount.toFixed(2));

    // If either USD price is missing, prices are stale
    if (!fiatValueTradeInput.data || !fiatValueTradeOutput.data) return true;

    // Check if the ratio between USD prices is significantly different from the ratio between amounts
    const tradeRatio = inputAmountDecimal / outputAmountDecimal;
    const usdRatio = fiatValueTradeInput.data / fiatValueTradeOutput.data;

    // Allow for some small deviation (e.g., 5%)
    return Math.abs(tradeRatio - usdRatio) > tradeRatio * 0.05;
  }, [
    trade?.inputAmount,
    trade?.outputAmount,
    fiatValueTradeInput.data,
    fiatValueTradeOutput.data,
  ]);

  const [stablecoinPriceImpact, preTaxStablecoinPriceImpact] = useMemo(
    () =>
      routeIsSyncing || !isClassicTrade(trade) || showWrap || usdPricesAreStale
        ? [undefined, undefined]
        : [
            computeFiatValuePriceImpact(
              fiatValueTradeInput.data,
              fiatValueTradeOutput.data
            ),
            computeFiatValuePriceImpact(
              fiatValueTradeInput.data,
              preTaxFiatValueTradeOutput.data
            ),
          ],
    [
      fiatValueTradeInput,
      fiatValueTradeOutput,
      preTaxFiatValueTradeOutput,
      routeIsSyncing,
      trade,
      showWrap,
      usdPricesAreStale, // Add to dependencies
    ]
  );

  const { onSwitchTokens, onCurrencySelection, onUserInput } =
    useSwapActionHandlers();
  const dependentField: Field =
    independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT;

  const handleTypeInput = useCallback(
    (value: string) => {
      onUserInput(Field.INPUT, value);
      maybeLogFirstSwapAction(trace);
    },
    [onUserInput, trace]
  );
  const handleTypeOutput = useCallback(
    (value: string) => {
      onUserInput(Field.OUTPUT, value);
      maybeLogFirstSwapAction(trace);
    },
    [onUserInput, trace]
  );

  const navigate = useNavigate();
  const swapIsUnsupported = useIsSwapUnsupported(
    currencies[Field.INPUT],
    currencies[Field.OUTPUT]
  );

  // reset if they close warning without tokens in params
  const handleDismissTokenWarning = useCallback(() => {
    setDismissTokenWarning(true);
    navigate("/swap/");
  }, [navigate]);

  // modal and loading
  const [
    { showConfirm, tradeToConfirm, swapError, swapResult },
    setSwapFormState,
  ] = useState<{
    showConfirm: boolean;
    tradeToConfirm?: InterfaceTrade;
    swapError?: Error;
    swapResult?: SwapResult;
  }>({
    showConfirm: false,
    tradeToConfirm: undefined,
    swapError: undefined,
    swapResult: undefined,
  });

  const previousConnectedChainId = usePrevious(connectedChainId);
  const previousPrefilledState = usePrevious(prefilledState);
  useEffect(() => {
    const chainChanged =
      previousConnectedChainId && previousConnectedChainId !== connectedChainId;
    const prefilledInputChanged =
      previousPrefilledState?.inputCurrency &&
      !prefilledState.inputCurrency?.equals(
        previousPrefilledState.inputCurrency
      );
    const prefilledOutputChanged =
      previousPrefilledState?.outputCurrency &&
      !prefilledState?.outputCurrency?.equals(
        previousPrefilledState.outputCurrency
      );

    if (chainChanged || prefilledInputChanged || prefilledOutputChanged) {
      // reset local state
      setSwapFormState({
        tradeToConfirm: undefined,
        swapError: undefined,
        showConfirm: false,
        swapResult: undefined,
      });
    }
  }, [
    connectedChainId,
    prefilledState.inputCurrency,
    prefilledState?.outputCurrency,
    previousConnectedChainId,
    previousPrefilledState,
  ]);

  const { formatCurrencyAmount } = useFormatter();
  const formattedAmounts = useMemo(
    () => ({
      [independentField]: typedValue,
      [dependentField]: showWrap
        ? parsedAmounts[independentField]?.toExact() ?? ""
        : formatCurrencyAmount({
            amount: parsedAmounts[dependentField],
            type: NumberType.SwapTradeAmount,
            placeholder: "",
          }),
    }),
    [
      dependentField,
      formatCurrencyAmount,
      independentField,
      parsedAmounts,
      showWrap,
      typedValue,
    ]
  );

  const userHasSpecifiedInputOutput = Boolean(
    currencies[Field.INPUT] &&
      currencies[Field.OUTPUT] &&
      parsedAmounts[independentField]?.greaterThan(JSBI.BigInt(0))
  );

  const maximumAmountIn = useMaxAmountIn(trade, allowedSlippage);
  const allowance = usePermit2Allowance(
    maximumAmountIn ??
      (parsedAmounts[Field.INPUT]?.currency.isToken
        ? (parsedAmounts[Field.INPUT] as CurrencyAmount<Token>)
        : undefined),
    isSupportedChain ? UNIVERSAL_ROUTER_ADDRESS(chainId) : undefined,
    trade?.fillType
  );

  const maxInputAmount: CurrencyAmount<Currency> | undefined = useMemo(
    () => maxAmountSpend(currencyBalances[Field.INPUT]),
    [currencyBalances]
  );
  const showMaxButton = Boolean(
    maxInputAmount?.greaterThan(0) &&
      !parsedAmounts[Field.INPUT]?.equalTo(maxInputAmount)
  );
  const swapFiatValues = useMemo(() => {
    return {
      amountIn: fiatValueTradeInput.data ?? undefined,
      amountOut: fiatValueTradeOutput.data ?? undefined,
      feeUsd: outputFeeFiatValue,
    };
  }, [fiatValueTradeInput.data, fiatValueTradeOutput.data, outputFeeFiatValue]);

  // the callback to execute the swap
  const swapCallback = useSwapCallback(
    trade,
    swapFiatValues,
    allowedSlippage,
    allowance.state === AllowanceState.ALLOWED
      ? allowance.permitSignature
      : undefined
  );

  const handleContinueToReview = useCallback(() => {
    setSwapFormState({
      tradeToConfirm: trade,
      swapError: undefined,
      showConfirm: true,
      swapResult: undefined,
    });
  }, [trade]);

  const clearSwapState = useCallback(() => {
    setSwapFormState((currentState) => ({
      ...currentState,
      swapError: undefined,
      swapResult: undefined,
    }));
  }, []);

  const handleSwap = useCallback(() => {
    if (!swapCallback) {
      return;
    }
    if (
      preTaxStablecoinPriceImpact &&
      !confirmPriceImpactWithoutFee(preTaxStablecoinPriceImpact)
    ) {
      return;
    }
    swapCallback()
      .then((result) => {
        setSwapFormState((currentState) => ({
          ...currentState,
          swapError: undefined,
          swapResult: result,
        }));
      })
      .catch((error) => {
        setSwapFormState((currentState) => ({
          ...currentState,
          swapError: error,
          swapResult: undefined,
        }));
      });
  }, [swapCallback, preTaxStablecoinPriceImpact]);

  const handleOnWrap = useCallback(async () => {
    if (!onWrap) {
      return;
    }
    try {
      const txHash = await onWrap();
      setSwapFormState((currentState) => ({
        ...currentState,
        swapError: undefined,
        txHash,
      }));
      onUserInput(Field.INPUT, "");
    } catch (error) {
      if (!didUserReject(error)) {
        sendAnalyticsEvent(SwapEventName.SWAP_ERROR, {
          wrapType,
          input: currencies[Field.INPUT],
          output: currencies[Field.OUTPUT],
        });
      } else {
        logger.debug("SwapForm", "handleOnWrap", "rejected wrap/unwrap");
      }
      setSwapFormState((currentState) => ({
        ...currentState,
        swapError: error,
        txHash: undefined,
      }));
    }
  }, [currencies, onUserInput, onWrap, wrapType]);

  // warnings on the greater of fiat value price impact and execution price impact
  const { priceImpactSeverity, largerPriceImpact } = useMemo(() => {
    if (!trade || !isClassicTrade(trade)) {
      return { priceImpactSeverity: 0, largerPriceImpact: undefined };
    }

    // Only calculate price impact if we have a valid trade with stable values
    const marketPriceImpact = trade.priceImpact
      ? computeRealizedPriceImpact(trade)
      : undefined;

    if (!marketPriceImpact) {
      return { priceImpactSeverity: 0, largerPriceImpact: undefined };
    }

    // Skip calculation if values are unreasonable (>90% impact is likely incorrect)
    if (marketPriceImpact.greaterThan(90)) {
      return { priceImpactSeverity: 0, largerPriceImpact: undefined };
    }

    const largerPriceImpact = largerPercentValue(
      marketPriceImpact,
      preTaxStablecoinPriceImpact
    );

    return {
      priceImpactSeverity: warningSeverity(largerPriceImpact),
      largerPriceImpact,
    };
  }, [preTaxStablecoinPriceImpact, trade]);

  const handleConfirmDismiss = useCallback(() => {
    setSwapFormState((currentState) => ({
      ...currentState,
      showConfirm: false,
    }));
    // If swap had a temporary router preference override, we want to reset it
    setSwapState((state) => ({
      ...state,
      routerPreferenceOverride: undefined,
    }));
    // If there was a swap, we want to clear the input
    if (swapResult) {
      onUserInput(Field.INPUT, "");
    }
  }, [onUserInput, setSwapState, swapResult]);

  const handleAcceptChanges = useCallback(() => {
    setSwapFormState((currentState) => ({
      ...currentState,
      tradeToConfirm: trade,
    }));
  }, [trade]);

  const handleInputSelect = useCallback(
    (inputCurrency: Currency) => {
      onCurrencySelection(Field.INPUT, inputCurrency);
      onCurrencyChange?.({
        inputCurrency,
        outputCurrency: currencyState.outputCurrency,
      });
      maybeLogFirstSwapAction(trace);
    },
    [onCurrencyChange, onCurrencySelection, currencyState, trace]
  );
  const inputCurrencyNumericalInputRef = useRef<HTMLInputElement>(null);

  const handleMaxInput = useCallback(() => {
    maxInputAmount && onUserInput(Field.INPUT, maxInputAmount.toExact());
    maybeLogFirstSwapAction(trace);
  }, [maxInputAmount, onUserInput, trace]);

  const handleOutputSelect = useCallback(
    (outputCurrency: Currency) => {
      onCurrencySelection(Field.OUTPUT, outputCurrency);
      onCurrencyChange?.({
        inputCurrency: currencyState.inputCurrency,
        outputCurrency,
      });
      maybeLogFirstSwapAction(trace);
    },
    [onCurrencyChange, onCurrencySelection, currencyState, trace]
  );

  const showPriceImpactWarning =
    isClassicTrade(trade) && largerPriceImpact && priceImpactSeverity > 3;

  const prevTrade = usePrevious(trade);
  useEffect(() => {
    if (!trade || prevTrade === trade) {
      return;
    } // no new swap quote to log

    sendAnalyticsEvent(SwapEventName.SWAP_QUOTE_RECEIVED, {
      ...formatSwapQuoteReceivedEventProperties(
        trade,
        allowedSlippage,
        swapQuoteLatency,
        outputFeeFiatValue
      ),
      ...trace,
    });
  }, [
    prevTrade,
    trade,
    trace,
    allowedSlippage,
    swapQuoteLatency,
    outputFeeFiatValue,
  ]);

  const showDetailsDropdown = Boolean(
    !showWrap &&
      userHasSpecifiedInputOutput &&
      (trade || routeIsLoading || routeIsSyncing)
  );

  const inputCurrency = currencies[Field.INPUT] ?? undefined;
  const outputCurrency = currencies[Field.OUTPUT] ?? undefined;
  const selectChain = useSelectChain();
  const switchingChain = useAppSelector(
    (state) => state.wallets.switchingChain
  );
  const targetChain = switchingChain ? switchingChain : undefined;
  const switchingChainIsSupported = useIsSupportedChainId(targetChain);
  // @ts-ignore
  const isUsingBlockedExtension = window.ethereum?.["isPocketUniverseZ"];

  return (
    <>
      <TokenSafetyModal
        isOpen={urlTokensNotInDefault.length > 0 && !dismissTokenWarning}
        token0={urlTokensNotInDefault[0]}
        token1={urlTokensNotInDefault[1]}
        onContinue={handleConfirmTokenWarning}
        onCancel={handleDismissTokenWarning}
        showCancel={true}
      />
      {trade && showConfirm && (
        <ConfirmSwapModal
          trade={trade}
          priceImpact={largerPriceImpact}
          inputCurrency={inputCurrency}
          originalTrade={tradeToConfirm}
          onAcceptChanges={handleAcceptChanges}
          onCurrencySelection={onCurrencySelection}
          swapResult={swapResult}
          allowedSlippage={allowedSlippage}
          clearSwapState={clearSwapState}
          onConfirm={handleSwap}
          allowance={allowance}
          swapError={swapError}
          onDismiss={handleConfirmDismiss}
          onXV2RetryWithClassic={() => {
            // Keep swap parameters but re-quote X trade with classic API
            setSwapState((state) => ({
              ...state,
              routerPreferenceOverride: RouterPreference.API,
            }));
            handleContinueToReview();
          }}
          fiatValueInput={fiatValueTradeInput}
          fiatValueOutput={fiatValueTradeOutput}
        />
      )}
      {showPriceImpactModal && showPriceImpactWarning && (
        <PriceImpactModal
          priceImpact={largerPriceImpact}
          onDismiss={() => setShowPriceImpactModal(false)}
          onContinue={() => {
            setShowPriceImpactModal(false);
            handleContinueToReview();
          }}
        />
      )}
      <div style={{ display: "relative" }}>
        <SwapSection>
          <Trace section={InterfaceSectionName.CURRENCY_INPUT_PANEL}>
            <SwapCurrencyInputPanel
              label={<Trans i18nKey="common.sell.label" />}
              disabled={disableTokenInputs}
              value={formattedAmounts[Field.INPUT]}
              showMaxButton={showMaxButton}
              currency={currencies[Field.INPUT] ?? null}
              onUserInput={handleTypeInput}
              onMax={handleMaxInput}
              fiatValue={showFiatValueInput ? fiatValueInput : undefined}
              onCurrencySelect={handleInputSelect}
              otherCurrency={currencies[Field.OUTPUT]}
              currencySearchFilters={SWAP_FORM_CURRENCY_SEARCH_FILTERS}
              id={InterfaceSectionName.CURRENCY_INPUT_PANEL}
              loading={independentField === Field.OUTPUT && routeIsSyncing}
              ref={inputCurrencyNumericalInputRef}
            />
          </Trace>
        </SwapSection>
        <ArrowWrapper clickable={isSupportedChain}>
          <Trace
            logPress
            eventOnTrigger={SwapEventName.SWAP_TOKENS_REVERSED}
            element={InterfaceElementName.SWAP_TOKENS_REVERSE_ARROW_BUTTON}
          >
            <ArrowContainer
              data-testid="swap-currency-button"
              onClick={() => {
                if (disableTokenInputs) {
                  return;
                }
                onSwitchTokens({
                  newOutputHasTax: inputTokenHasTax,
                  previouslyEstimatedOutput: formattedAmounts[dependentField],
                });
                maybeLogFirstSwapAction(trace);
              }}
              color={theme.neutral1}
            >
              <ArrowDown size="16" color={theme.neutral1} />
            </ArrowContainer>
          </Trace>
        </ArrowWrapper>
      </div>
      <AutoColumn gap="xs">
        <div>
          <OutputSwapSection>
            <Trace section={InterfaceSectionName.CURRENCY_OUTPUT_PANEL}>
              <SwapCurrencyInputPanel
                value={formattedAmounts[Field.OUTPUT]}
                disabled={disableTokenInputs}
                onUserInput={handleTypeOutput}
                label={<Trans i18nKey="common.buy.label" />}
                showMaxButton={false}
                hideBalance={false}
                fiatValue={showFiatValueOutput ? fiatValueOutput : undefined}
                priceImpact={stablecoinPriceImpact}
                currency={currencies[Field.OUTPUT] ?? null}
                onCurrencySelect={handleOutputSelect}
                otherCurrency={currencies[Field.INPUT]}
                currencySearchFilters={SWAP_FORM_CURRENCY_SEARCH_FILTERS}
                id={InterfaceSectionName.CURRENCY_OUTPUT_PANEL}
                loading={independentField === Field.INPUT && routeIsSyncing}
                numericalInputSettings={{
                  // We disable numerical input here if the selected token has tax, since we cannot guarantee exact_outputs for FOT tokens
                  disabled: inputTokenHasTax || outputTokenHasTax,
                  // Focus the input currency panel if the user tries to type into the disabled output currency panel
                  onDisabledClick: () =>
                    inputCurrencyNumericalInputRef.current?.focus(),
                  disabledTooltipBody: (
                    <OutputTaxTooltipBody
                      currencySymbol={
                        currencies[
                          inputTokenHasTax ? Field.INPUT : Field.OUTPUT
                        ]?.symbol
                      }
                    />
                  ),
                }}
              />
            </Trace>
          </OutputSwapSection>
        </div>

        {showPriceImpactWarning && (
          <PriceImpactWarning priceImpact={largerPriceImpact} />
        )}
        <div>
          {swapIsUnsupported ? (
            <ButtonPrimary $borderRadius="16px" disabled={true}>
              <ThemedText.DeprecatedMain mb="4px">
                <Trans i18nKey="common.unsupportedAsset_one" />
              </ThemedText.DeprecatedMain>
            </ButtonPrimary>
          ) : !multichainUXEnabled && switchingChain ? (
            <ButtonPrimary $borderRadius="16px" disabled={true}>
              <Trans
                i18nKey="common.connectingToChain"
                values={{
                  chainName: switchingChainIsSupported
                    ? CHAIN_INFO[targetChain]?.label
                    : "",
                }}
              />
            </ButtonPrimary>
          ) : isDisconnected ? (
            <Trace
              logPress
              eventOnTrigger={InterfaceEventName.CONNECT_WALLET_BUTTON_CLICKED}
              properties={{
                received_swap_quote: getIsReviewableQuote(
                  trade,
                  tradeState,
                  swapInputError
                ),
              }}
              element={InterfaceElementName.CONNECT_WALLET_BUTTON}
            >
              <ButtonLight
                onClick={accountDrawer.open}
                fontWeight={535}
                $borderRadius="16px"
              >
                <Trans i18nKey="common.connectWallet.button" />
              </ButtonLight>
            </Trace>
          ) : !multichainUXEnabled &&
            chainId &&
            chainId !== connectedChainId ? (
            <ButtonPrimary
              $borderRadius="16px"
              onClick={async () => {
                try {
                  await selectChain(chainId);
                } catch (error) {
                  if (didUserReject(error)) {
                    // Ignore error, which keeps the user on the previous chain.
                  } else {
                    // TODO(WEB-3306): This UX could be improved to show an error state.
                    throw error;
                  }
                }
              }}
            >
              <Trans
                i18nKey="common.connectToChain.button"
                values={{
                  chainName: isSupportedChain ? CHAIN_INFO[chainId].label : "",
                }}
              />
            </ButtonPrimary>
          ) : showWrap ? (
            <ButtonPrimary
              $borderRadius="16px"
              disabled={Boolean(wrapInputError)}
              onClick={handleOnWrap}
              fontWeight={535}
              data-testid="wrap-button"
            >
              {wrapInputError ? (
                <WrapErrorText
                  wrapInputError={wrapInputError}
                  tokenSymbolBase={inputCurrency?.symbol ?? ""}
                  tokenSymbolWrapped={outputCurrency?.symbol ?? ""}
                />
              ) : wrapType === WrapType.WRAP ? (
                <Trans i18nKey="common.wrap.button" />
              ) : wrapType === WrapType.UNWRAP ? (
                <Trans i18nKey="common.unwrap.button" />
              ) : wrapType === WrapType.APPROVE_AND_WRAP ? (
                isApproving ? (
                  <Dots>
                    <Trans i18nKey="common.approving" />
                  </Dots>
                ) : (
                  <Trans i18nKey="common.approveAndWrap.button" />
                )
              ) : wrapType === WrapType.APPROVE_AND_UNWRAP ? (
                isApproving ? (
                  <Dots>
                    <Trans i18nKey="common.approving" />
                  </Dots>
                ) : (
                  <Trans i18nKey="common.approveAndUnwrap.button" />
                )
              ) : null}
            </ButtonPrimary>
          ) : routeNotFound &&
            userHasSpecifiedInputOutput &&
            !routeIsLoading &&
            !routeIsSyncing ? (
            <GrayCard style={{ textAlign: "center" }}>
              <ThemedText.DeprecatedMain mb="4px">
                <Trans i18nKey="swap.form.insufficientLiquidity" />
              </ThemedText.DeprecatedMain>
            </GrayCard>
          ) : (
            <Trace logPress element={InterfaceElementName.SWAP_BUTTON}>
              <ButtonError
                onClick={async () => {
                  const inputChainId = trade?.inputAmount?.currency?.chainId;
                  if (inputChainId && inputChainId !== connectedChainId) {
                    await selectChain(inputChainId);
                  }
                  showPriceImpactWarning
                    ? setShowPriceImpactModal(true)
                    : handleContinueToReview();
                }}
                id="swap-button"
                data-testid="swap-button"
                disabled={
                  isUsingBlockedExtension ||
                  !getIsReviewableQuote(trade, tradeState, swapInputError)
                }
                error={
                  !swapInputError &&
                  priceImpactSeverity > 2 &&
                  allowance.state === AllowanceState.ALLOWED
                }
              >
                <Text fontSize={20}>
                  {swapInputError ? (
                    swapInputError
                  ) : routeIsSyncing || routeIsLoading ? (
                    <Trans i18nKey="common.swap" />
                  ) : priceImpactSeverity > 2 ? (
                    <Trans i18nKey="swap.form.swapAnywayAction" />
                  ) : (
                    <Trans i18nKey="common.swap" />
                  )}
                </Text>
              </ButtonError>
            </Trace>
          )}
          {showDetailsDropdown && (
            <SwapDetailsDropdown
              trade={trade}
              syncing={routeIsSyncing}
              loading={routeIsLoading}
              allowedSlippage={allowedSlippage}
              priceImpact={largerPriceImpact}
            />
          )}
          {isUsingBlockedExtension && <SwapNotice />}
        </div>
        <SwapDisclaimer />
      </AutoColumn>
    </>
  );
}

function SwapNotice() {
  const theme = useTheme();
  return (
    <Row
      align="flex-start"
      gap="md"
      backgroundColor={theme.surface2}
      marginTop="12px"
      borderRadius="12px"
      padding="16px"
    >
      <Row
        width="auto"
        borderRadius="16px"
        backgroundColor={theme.critical2}
        padding="8px"
      >
        <ErrorIcon />
      </Row>

      <Column flex="10" gap="sm">
        <ThemedText.SubHeader>Blocked Extension</ThemedText.SubHeader>
        <ThemedText.BodySecondary lineHeight="22px">
          <Trans
            i18nKey="swap.form.pocketUniverseExtension.warning"
            components={{
              termsLink: (
                <ExternalLink href="https://docs.taraswap.info">
                  <Trans i18nKey="common.termsOfService" />
                </ExternalLink>
              ),
            }}
          />
        </ThemedText.BodySecondary>
      </Column>
    </Row>
  );
}
